import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { AiResult } from '../../../../../models/AiResult'
import { SyncStatus, SyncStatusView } from './models'
import { selectTasks } from '../../../../../store/task/task'
import { selectMeetingSummarySyncStatus } from '../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { selectAiResults } from '../../../../../store/meetingSummary/aiResults'
import { selectAiFeatures } from '../../../../../store/meetingSummary/aiFeatures'
import { AiFeature } from '../../../../../models/AiFeature'
import { Task } from '../../../../../models/MeetingSupport'
import { selectContactChanges } from '../../../../../store/meetingSummary/contactUpdate'
import { getContactChangeSyncStatusViews } from './utils'

const flattenTaskSync = (tasks: Task[]): SyncStatus[] => tasks.flatMap((t) => t?.syncStatus ?? [])

const getAIResultSyncStatusView = (aiResults: AiResult[], aiFeatures: AiFeature[]): SyncStatusView[] =>
  aiResults.flatMap((r) => {
    return r.syncStatus.map((s) => {
      return {
        ...s,
        type: 'aiResult',
        name: aiFeatures.find((aif) => aif.id === r.aiFeatureId)?.name ?? null,
      } as SyncStatusView
    })
  })

const unifySyncStatuses = (
  roomSessionSyncStatuses: SyncStatus[] = [],
  taskSyncStatuses: SyncStatus[] = [],
  aiResultSyncStatuses: SyncStatusView[] = [],
  aiAgentResultSyncStatuses: SyncStatus[] = [],
  contactChangesSyncStatuses: SyncStatusView[] = [],
): SyncStatusView[] => {
  return [
    ...roomSessionSyncStatuses.map(
      (s): SyncStatusView => ({
        ...s,
        type: 'export',
      }),
    ),
    ...taskSyncStatuses.map(
      (s): SyncStatusView => ({
        ...s,
        type: 'task',
      }),
    ),
    ...aiResultSyncStatuses,
    ...aiAgentResultSyncStatuses.map(
      (s): SyncStatusView => ({
        ...s,
        type: 'meetingSummary',
      }),
    ),
    ...contactChangesSyncStatuses,
  ]
}

export const useSyncStatuses = () => {
  const roomSession = useAppSelector(selectRoomSession)
  const tasks = useAppSelector(selectTasks)
  const aiResults = useAppSelector(selectAiResults)
  const aiAgentResultSyncStatus = useAppSelector(selectMeetingSummarySyncStatus)
  const aiFeatures = useAppSelector(selectAiFeatures)
  const contactChanges = useAppSelector(selectContactChanges)

  const { users } = roomSession ?? {}

  const taskSyncStatuses = flattenTaskSync(tasks)
  const aiResultSyncStatuses = getAIResultSyncStatusView(aiResults, aiFeatures)
  const contactChangesSyncStatuses = getContactChangeSyncStatusViews(contactChanges, users ?? [])

  const syncStatus = unifySyncStatuses(
    roomSession?.syncStatus ?? [],
    taskSyncStatuses,
    aiResultSyncStatuses,
    aiAgentResultSyncStatus,
    contactChangesSyncStatuses,
  )
  const sortedSyncStatus = syncStatus.sort((a, b) => Date.parse(b.syncedAt) - Date.parse(a.syncedAt))

  return {
    syncStatuses: sortedSyncStatus,
    meetingSummarySyncStatus: sortedSyncStatus.filter((s) => s.type === 'meetingSummary'),
    aiResultsSyncStatus: sortedSyncStatus.filter((s) => s.type === 'aiResult'),
    taskSyncStatuses: sortedSyncStatus.filter((s) => s.type === 'task'),
    roomSessionSyncStatus: sortedSyncStatus.filter((s) => s.type === 'export'),
    contactUpdateSyncStatus: sortedSyncStatus.filter((s) => s.type === 'updateContact'),
    contactCreateSyncStatus: sortedSyncStatus.filter((s) => s.type === 'createContact'),
  }
}
